import { Link, Outlet, useLocation, useOutletContext } from 'react-router-dom';

import { SCANNER_MODES, type ScannerMode } from './modes';
import { type PendingSyncStatus, usePendingSyncStatus } from './pendingSync';
import { scannerModulePath, SCANNER_PATH } from './routes';
import { ScanInScreen } from './ScanInScreen';
import { ScannerOptions } from './ScannerOptions';
import { ScanOutScreen } from './ScanOutScreen';
import { StoreStagingScreen } from './StoreStagingScreen';
import { touchTargetStyle } from './touch';
import { useConnectivity } from './useConnectivity';
import { useOfflineSync, type OfflineSyncController } from './useOfflineSync';
import './scanner.css';
import { SessionPersistenceNotice } from '../lib/auth/SessionControls';

/**
 * `ScannerShell` เป็น **layout** ของเครื่องสแกน ไม่ใช่หน้าจอที่รู้จักทุกโหมด
 * อีกต่อไป (D-109 ข้อ 1): แถบบน (ป้ายออนไลน์/คิว + ตัวเลือก + ปุ่มกลับเมนูตอนอยู่
 * ในโมดูล) + `<Outlet/>` ที่สลับระหว่างเมนู (`ScannerMenu`, index route) กับ
 * โมดูลเต็มจอ (`ScannerModuleScreen` ด้านล่าง ผ่าน route ย่อยของ `modes.ts`) +
 * แถบล่าง ป้ายสถานะสองอันต้องติดตัวไปทุกมุมมองเพราะอยู่ที่ layout ชั้นเดียวนี้
 * ไม่ใช่ของหน้าจอใดหน้าจอหนึ่ง (D-109 ข้อ 3)
 */

export interface ScannerOutletContext {
  readonly pendingSync: PendingSyncStatus;
  readonly offlineSync: OfflineSyncController;
}

export function ScannerShell() {
  const online = useConnectivity();
  const pendingSync = usePendingSyncStatus();
  /*
   * ตัวระบายคิวผูกกับ `online` ตัวเดียวกับที่หัวจอแสดง — การกลับมาออนไลน์จึงเป็น
   * เหตุการณ์เดียวกันทั้งสองที่ ไม่ใช่สองความจริงที่อาจไม่ตรงกัน (D-078)
   */
  const offlineSync = useOfflineSync(online);
  const { pathname } = useLocation();
  const currentMode =
    SCANNER_MODES.find((option) => pathname === scannerModulePath(option.path)) ?? null;
  const queueUnavailable = pendingSync.availability === 'UNAVAILABLE';
  const pendingLabel =
    pendingSync.availability === 'READY'
      ? `รอซิงค์ ${pendingSync.count} รายการ`
      : queueUnavailable
        ? 'คิวในเครื่องใช้งานไม่ได้'
        : 'กำลังอ่านคิวในเครื่อง';

  return (
    <div className="cap-scanner" data-testid="scanner-shell">
      <header className="cap-scanner__top">
        <div className="cap-scanner__title">
          {currentMode !== null ? (
            <>
              {/*
                ปุ่มกลับเมนูของจริงบนแถบบน ไม่ใช่ลิงก์เล็ก ๆ ท้ายหน้า — ขนาดไม่
                น้อยกว่า touch target ขั้นต่ำเหมือนปุ่มอื่นของหน้างาน คนสวมถุงมือ
                กดลิงก์ขนาดตัวหนังสือไม่โดน (D-109 คำอธิบายท้าย)
              */}
              <Link
                className="cap-scanner__back"
                style={touchTargetStyle}
                data-testid="scanner-back-to-menu"
                to={SCANNER_PATH}
              >
                ← เมนู
              </Link>
              <span className="cap-scanner__device" data-testid="scanner-selected-mode">
                {currentMode.icon} {currentMode.label}
              </span>
            </>
          ) : (
            <>
              <span className="cap-scanner__app">CAP ERP · หน้างาน</span>
              <span className="cap-scanner__device">ยังไม่ผูกอุปกรณ์กับไลน์ผลิต</span>
            </>
          )}
        </div>
        <div className="cap-scanner__status">
          <span
            className={`cap-pill ${online ? 'cap-pill--online' : 'cap-pill--offline'}`}
            data-testid="connectivity-status"
            role="status"
          >
            {online ? 'ออนไลน์' : 'ออฟไลน์'}
          </span>
          <span
            className={`cap-pill ${queueUnavailable ? 'cap-pill--queue-down' : 'cap-pill--queue'}`}
            data-testid="pending-sync-count"
            data-queue-availability={pendingSync.availability}
            role="status"
          >
            {pendingLabel}
          </span>
          <ScannerOptions />
        </div>
      </header>

      <SessionPersistenceNotice className="cap-scanner__session-note" />

      <main className="cap-scanner__body">
        <Outlet context={{ pendingSync, offlineSync } satisfies ScannerOutletContext} />
      </main>

      <footer className="cap-scanner__foot">
        <Link className="cap-scanner__link" style={touchTargetStyle} to="/">
          กลับหน้าจอ Desktop
        </Link>
      </footer>
    </div>
  );
}

/**
 * หน้าจอของโมดูลหนึ่ง — เต็มจอ ไม่มีปุ่มเลือกโมดูลอื่นค้างอยู่ (D-109 headline)
 *
 * รับ `pendingSync` จาก `<Outlet context>` ของ `ScannerShell` ตัวเดียวกับที่
 * แถบบนใช้ — ตัวเลข "รอซิงค์" บนหัวจอกับในฟอร์มจึงเป็นค่าเดียวกันเสมอ ไม่ใช่สอง
 * ค่าที่อ่านคนละรอบแล้วบังเอิญตรงกัน (`ScanCommandForm.tsx`)
 */
export function ScannerModuleScreen({ mode }: { readonly mode: ScannerMode }) {
  const { pendingSync } = useOutletContext<ScannerOutletContext>();

  return (
    <section aria-label="บันทึกงานหน้างาน" className="cap-scanner__scan">
      {mode === 'STORE_STAGING' ? <StoreStagingScreen pendingSync={pendingSync} /> : null}
      {mode === 'SCAN_IN' ? <ScanInScreen pendingSync={pendingSync} /> : null}
      {mode === 'SCAN_OUT' ? <ScanOutScreen pendingSync={pendingSync} /> : null}
      <p className="cap-scanner__note" data-testid="scanner-capture-note">
        ยังไม่เชื่อมเครื่องอ่านบาร์โค้ดในบิลด์นี้ — ป้อนค่าที่สแกนได้ด้วยการพิมพ์ไปก่อน
        ค่าที่กดส่งจะถูกเก็บเป็นคำสั่งในเครื่องและรอซิงค์ ไม่ได้ถูกส่งถึงเซิร์ฟเวอร์ทันที
      </p>
    </section>
  );
}
